import React, { useState, useEffect } from 'react';
import apiClient from '../../api/apiClient';

const FlowsManager = ({ projectId, onEditFlow, onCreateFlow, canManage = true, hasReadOnlyAccess = false }) => {
  const [flows, setFlows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [availableIntents, setAvailableIntents] = useState([]);
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [flowToDuplicate, setFlowToDuplicate] = useState(null);
  const [duplicateForm, setDuplicateForm] = useState({
    name: '',
    description: '',
    linkedIntents: []
  });
  const [duplicateLoading, setDuplicateLoading] = useState(false);
  
  // Sync to production state
  const [showSyncModal, setShowSyncModal] = useState(false);
  const [flowToSync, setFlowToSync] = useState(null);
  const [syncLoading, setSyncLoading] = useState(false);
  const [syncResult, setSyncResult] = useState(null);

  useEffect(() => {
    if (projectId) {
      fetchFlows();
      fetchAvailableIntents();
    }
  }, [projectId]);

  // Debug effect to track modal state changes
  useEffect(() => {
    console.log('[FlowsManager] Modal state changed:', showSyncModal);
    if (showSyncModal) {
      console.log('[FlowsManager] Modal should be visible now');
      // Check if modal element exists in DOM after render
      setTimeout(() => {
        const modalElement = document.getElementById('sync-modal-backdrop');
        console.log('[FlowsManager] Modal element in DOM:', modalElement);
        if (modalElement) {
          console.log('[FlowsManager] Modal element style:', modalElement.style.cssText);
          console.log('[FlowsManager] Modal element computed styles:', {
            display: window.getComputedStyle(modalElement).display,
            visibility: window.getComputedStyle(modalElement).visibility,
            opacity: window.getComputedStyle(modalElement).opacity,
            zIndex: window.getComputedStyle(modalElement).zIndex,
            position: window.getComputedStyle(modalElement).position
          });
          
          // Try scrolling to modal and flashing it
          modalElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
          modalElement.style.backgroundColor = 'red';
          setTimeout(() => {
            modalElement.style.backgroundColor = 'rgba(0, 0, 0, 0.5)';
          }, 500);
        } else {
          console.error('[FlowsManager] Modal element NOT found in DOM!');
        }
      }, 100);
    }
  }, [showSyncModal]);

  const fetchFlows = async () => {
    try {
      setLoading(true);
      console.log('FlowsManager: Fetching flows for project:', projectId);
      const response = await apiClient.get(`/flow-builder/projects/${projectId}/flows`);
      console.log('FlowsManager: Flows response:', response);
      
      // Ensure we always set an array
      const flowsData = response.data?.data?.flows || response.data?.flows || response.data || [];
      setFlows(Array.isArray(flowsData) ? flowsData : []);
      setError(null);
    } catch (error) {
      console.error('FlowsManager: Error fetching flows:', error);
      // Ensure flows is always an array even on error
      setFlows([]);
      
      // Handle the case where the endpoint doesn't exist yet
      if (error.response?.status === 404) {
        setError('Flow Builder API endpoints are not available yet');
      } else {
        setError('Failed to fetch flows');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCreateFlow = () => {
    if (onCreateFlow) {
      onCreateFlow();
    } else {
      // Fallback: trigger flow creation with null flow (new flow)
      if (onEditFlow) {
        onEditFlow(null);
      }
    }
  };

  const handleEditFlow = (flow) => {
    if (onEditFlow) {
      onEditFlow(flow);
    } else {
      console.log('Edit flow:', flow);
    }
  };

  const handleDeleteFlow = async (flowId) => {
    if (!window.confirm('Are you sure you want to delete this flow?')) {
      return;
    }

    try {
      await apiClient.delete(`/flow-builder/projects/${projectId}/flows/${flowId}`);
      await fetchFlows();
    } catch (error) {
      console.error('Error deleting flow:', error);
      setError('Failed to delete flow');
    }
  };

  const fetchAvailableIntents = async () => {
    try {
      const response = await apiClient.get(`/flow-builder/projects/${projectId}/intents`);
      const intents = response.data?.data?.intents || response.data?.intents || [];
      setAvailableIntents(Array.isArray(intents) ? intents : []);
    } catch (error) {
      console.error('Error fetching intents:', error);
      setAvailableIntents([]);
    }
  };

  const handleDuplicateFlow = (flow) => {
    setFlowToDuplicate(flow);
    setDuplicateForm({
      name: `${flow.name} (Copy)`,
      description: flow.description || '',
      linkedIntents: []
    });
    setShowDuplicateModal(true);
  };

  const handleDuplicateFormChange = (field, value) => {
    setDuplicateForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const getUsedIntentIds = () => {
    // Get all intent IDs that are currently linked to other flows (excluding the one being duplicated)
    const usedIntentIds = new Set();
    flows.forEach(flow => {
      if (flow._id !== flowToDuplicate?._id && flow.linkedIntents) {
        flow.linkedIntents.forEach(intent => {
          const intentId = typeof intent === 'object' ? intent._id : intent;
          usedIntentIds.add(intentId);
        });
      }
    });
    return usedIntentIds;
  };

  const getAvailableIntentsForDuplication = () => {
    const usedIntentIds = getUsedIntentIds();
    return availableIntents.filter(intent => !usedIntentIds.has(intent._id));
  };

  const handleSubmitDuplicate = async () => {
    if (!duplicateForm.name.trim()) {
      setError('Flow name is required');
      return;
    }

    if (duplicateForm.linkedIntents.length === 0) {
      setError('At least one linked intent is required');
      return;
    }

    setDuplicateLoading(true);
    setError(null);

    try {
      await apiClient.post(`/flow-builder/projects/${projectId}/flows/${flowToDuplicate._id}/duplicate`, {
        newName: duplicateForm.name.trim(),
        newDescription: duplicateForm.description.trim(),
        linkedIntents: duplicateForm.linkedIntents
      });

      // Refresh flows list
      await fetchFlows();
      
      // Close modal and reset form
      setShowDuplicateModal(false);
      setFlowToDuplicate(null);
      setDuplicateForm({
        name: '',
        description: '',
        linkedIntents: []
      });
    } catch (error) {
      console.error('Error duplicating flow:', error);
      setError(error.response?.data?.message || 'Failed to duplicate flow');
    } finally {
      setDuplicateLoading(false);
    }
  };

  const handleCancelDuplicate = () => {
    setShowDuplicateModal(false);
    setFlowToDuplicate(null);
    setDuplicateForm({
      name: '',
      description: '',
      linkedIntents: []
    });
    setError(null);
  };

  // Sync to production functionality
  const shouldShowSyncButton = () => {
    const environment = process.env.REACT_APP_ENVIRONMENT;
    console.log('[FlowsManager] Environment check:', { 
      environment, 
      shouldShow: environment === 'local' || environment === 'staging' 
    });
    return environment === 'local' || environment === 'staging';
  };

  const handleSyncToProduction = (flow) => {
    console.log('[FlowsManager] Sync button clicked for flow:', flow);
    console.log('[FlowsManager] Setting modal state...');
    
    setFlowToSync(flow);
    setShowSyncModal(true);
    setSyncResult(null);
    setError(null);
    
    console.log('[FlowsManager] Modal state should be set to true');
  };

  const handleConfirmSync = async () => {
    if (!flowToSync) return;

    setSyncLoading(true);
    setError(null);

    try {
      const response = await apiClient.post(
        `/flow-builder/projects/${projectId}/flows/${flowToSync._id}/sync-to-production`
      );

      setSyncResult(response.data);
      console.log('Flow synced successfully:', response.data);
      
      // Auto-close modal after 3 seconds on success
      setTimeout(() => {
        handleCancelSync();
      }, 3000);

    } catch (error) {
      console.error('Error syncing flow:', error);
      setError(error.response?.data?.message || 'Failed to sync flow to production');
    } finally {
      setSyncLoading(false);
    }
  };

  const handleCancelSync = () => {
    setShowSyncModal(false);
    setFlowToSync(null);
    setSyncResult(null);
    setError(null);
  };

  // Ensure flows is always an array before filtering
  const safeFlows = Array.isArray(flows) ? flows : [];
  const filteredFlows = safeFlows.filter(flow =>
    flow?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    flow?.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Flows</h2>
          <p className="text-gray-600">Manage conversational flows and step sequences</p>
          {/* Debug info */}
          <div className="text-xs text-gray-500">
            Environment: {process.env.REACT_APP_ENVIRONMENT || 'undefined'} | 
            Show Sync: {shouldShowSyncButton() ? 'Yes' : 'No'} | 
            Modal: {showSyncModal ? 'Open' : 'Closed'}
          </div>
        </div>
        <div className="flex gap-2">
          {/* Debug button for testing modal */}
          {shouldShowSyncButton() && (
            <div className="flex gap-1">
              <button
                onClick={() => {
                  console.log('[FlowsManager] Alert test button clicked');
                  alert('This is a test alert to verify click handlers work');
                }}
                className="bg-green-600 text-white px-2 py-2 rounded-lg hover:bg-green-700 transition-colors text-xs"
              >
                ✅ Test Alert
              </button>
              <button
                onClick={() => {
                  console.log('[FlowsManager] Debug button clicked - forcing modal open');
                  setShowSyncModal(true);
                  setFlowToSync({ name: 'Test Flow', _id: 'test-id', steps: [] });
                  // Also force a simple body style change to verify DOM manipulation
                  document.body.style.backgroundColor = 'lightblue';
                  setTimeout(() => {
                    document.body.style.backgroundColor = '';
                  }, 1000);
                }}
                className="bg-purple-600 text-white px-2 py-2 rounded-lg hover:bg-purple-700 transition-colors text-xs"
              >
                🚀 Test Modal
              </button>
            </div>
          )}
          {canManage && (
            <button
              onClick={handleCreateFlow}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
            >
              Create Flow
            </button>
          )}
        </div>
      </div>

      {/* Search */}
      <div className="relative">
        <input
          type="text"
          placeholder="Search flows..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
        
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      {/* Flows Grid */}
      {filteredFlows.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <div className="mx-auto h-12 w-12 text-gray-400">
            🌊
          </div>
          <h3 className="mt-2 text-sm font-medium text-gray-900">No flows</h3>
          <p className="mt-1 text-sm text-gray-500">
            {searchTerm ? 'No flows match your search.' : 'Get started by creating your first flow.'}
          </p>
          {!searchTerm && canManage && (
            <div className="mt-6">
              <button
                onClick={handleCreateFlow}
                className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
              >
                Create Flow
              </button>
            </div>
          )}
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {filteredFlows.map((flow) => (
            <div
              key={flow._id}
              className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
            >
              <div className="flex justify-between items-start mb-3">
                <h3 className="text-lg font-semibold text-gray-900 truncate">
                  {flow.name}
                </h3>
                <div className="flex space-x-1">
                  {canManage ? (
                    <>
                      <button
                        onClick={() => handleEditFlow(flow)}
                        className="text-gray-400 hover:text-blue-600 p-1 btn btn-secondary"
                        title="Edit flow"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDuplicateFlow(flow)}
                        className="text-gray-400 hover:text-green-600 p-1 btn btn-secondary"
                        title="Duplicate flow"
                      >
                        Copy
                      </button>
                      {shouldShowSyncButton() && (
                        <button
                          onClick={(e) => {
                            console.log('[FlowsManager] Sync button click event:', e);
                            console.log('[FlowsManager] Flow being synced:', flow);
                            e.preventDefault();
                            e.stopPropagation();
                            handleSyncToProduction(flow);
                          }}
                          className="text-gray-400 hover:text-purple-600 p-1 btn btn-secondary"
                          title="Sync to production"
                          style={{ backgroundColor: '#f3f4f6', border: '1px solid #d1d5db' }}
                        >
                          🚀 Sync
                        </button>
                      )}
                      <button
                        onClick={() => handleDeleteFlow(flow._id)}
                        className="text-gray-400 hover:text-red-600 p-1 btn btn-danger"
                        title="Delete flow"
                      >
                        Delete
                      </button>
                    </>
                  ) : (
                    <button
                      onClick={() => handleEditFlow(flow)}
                      className="text-gray-400 hover:text-blue-600 p-1 btn btn-secondary"
                      title="View flow"
                    >
                      View
                    </button>
                  )}
                </div>
              </div>

              {flow.description && (
                <p className="text-gray-600 text-sm mb-3 line-clamp-2">
                  {flow.description}
                </p>
              )}

              <div className="flex justify-between items-center text-sm text-gray-500">
                <span>
                  {flow.steps?.length || 0} steps
                </span>
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                  flow.active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                }`}>
                  {flow.active ? 'Active' : 'Inactive'}
                </span>
              </div>

              {flow.linkedIntents && flow.linkedIntents.length > 0 && (
                <div className="mt-3 pt-3 border-t border-gray-100">
                  <div className="text-xs text-gray-500 mb-1">Linked Intents:</div>
                  <div className="flex flex-wrap gap-1">
                    {flow.linkedIntents.slice(0, 3).map((intent, index) => (
                      <span
                        key={index}
                        className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                      >
                        {intent.name || intent}
                      </span>
                    ))}
                    {flow.linkedIntents.length > 3 && (
                      <span className="text-xs text-gray-500">
                        +{flow.linkedIntents.length - 3} more
                      </span>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Duplicate Flow Modal */}
      {showDuplicateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Duplicate Flow: {flowToDuplicate?.name}
            </h3>
            
            <div className="space-y-4">
              {/* Flow Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Flow Name *
                </label>
                <input
                  type="text"
                  value={duplicateForm.name}
                  onChange={(e) => handleDuplicateFormChange('name', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Enter flow name"
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  value={duplicateForm.description}
                  onChange={(e) => handleDuplicateFormChange('description', e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Describe what this flow does"
                />
              </div>

              {/* Linked Intents */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Linked Intents *
                </label>
                {getAvailableIntentsForDuplication().length === 0 ? (
                  <div className="text-sm text-red-600 p-3 bg-red-50 rounded-lg">
                    No available intents. All intents are already linked to other flows.
                  </div>
                ) : (
                  <select
                    multiple
                    value={duplicateForm.linkedIntents}
                    onChange={(e) => {
                      const selectedOptions = Array.from(e.target.selectedOptions, option => option.value);
                      handleDuplicateFormChange('linkedIntents', selectedOptions);
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    size={Math.min(getAvailableIntentsForDuplication().length, 4)}
                  >
                    {getAvailableIntentsForDuplication().map((intent) => (
                      <option key={intent._id} value={intent._id}>
                        {intent.name}
                        {intent.description && ` - ${intent.description.substring(0, 50)}${intent.description.length > 50 ? '...' : ''}`}
                      </option>
                    ))}
                  </select>
                )}
                <p className="text-xs text-gray-500 mt-1">
                  Hold Ctrl/Cmd to select multiple intents
                </p>
              </div>

              {/* Selected Intents Display */}
              {duplicateForm.linkedIntents.length > 0 && (
                <div className="mt-2">
                  <div className="text-sm font-medium text-gray-700 mb-2">Selected Intents:</div>
                  <div className="flex flex-wrap gap-1">
                    {duplicateForm.linkedIntents.map((intentId) => {
                      const intent = availableIntents.find(i => i._id === intentId);
                      return (
                        <span
                          key={intentId}
                          className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                        >
                          {intent?.name || 'Unknown'}
                        </span>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            {/* Error Message */}
            {error && (
              <div className="mt-4 bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-lg text-sm">
                {error}
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={handleCancelDuplicate}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                disabled={duplicateLoading}
              >
                Cancel
              </button>
              <button
                onClick={handleSubmitDuplicate}
                disabled={duplicateLoading || !duplicateForm.name.trim() || duplicateForm.linkedIntents.length === 0}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {duplicateLoading ? 'Duplicating...' : 'Duplicate Flow'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Sync to Production Modal */}
      {(() => {
        console.log('[FlowsManager] Modal render check:', { 
          showSyncModal, 
          flowToSync: flowToSync?.name,
          syncResult: !!syncResult 
        });
        return null;
      })()}
      {showSyncModal && (
        <div 
          id="sync-modal-backdrop"
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center"
          style={{ 
            zIndex: 9999,
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            backgroundColor: 'rgba(0, 0, 0, 0.5)'
          }}
          onClick={(e) => {
            console.log('[FlowsManager] Modal backdrop clicked');
            if (e.target === e.currentTarget) {
              handleCancelSync();
            }
          }}
          ref={(el) => {
            if (el) {
              console.log('[FlowsManager] Modal DOM element created:', el);
              console.log('[FlowsManager] Modal computed styles:', window.getComputedStyle(el));
            }
          }}
        >
          <div 
            className="bg-white rounded-lg p-6 w-full max-w-md mx-4"
            style={{
              backgroundColor: 'white',
              borderRadius: '8px',
              padding: '24px',
              maxWidth: '28rem',
              margin: '0 1rem',
              boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
              position: 'relative',
              zIndex: 10000,
              border: '3px solid red', // Temporary debug border
              outline: '2px solid blue' // Extra visibility
            }}
            onClick={(e) => {
              console.log('[FlowsManager] Modal content clicked');
              e.stopPropagation();
            }}
          >
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              {syncResult ? 'Sync Complete' : 'Sync to Production'}
            </h3>
            
            {!syncResult ? (
              <div className="space-y-4">
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <span className="text-yellow-500 text-lg">⚠️</span>
                    </div>
                    <div className="ml-3">
                      <h3 className="text-sm font-medium text-yellow-800">
                        Important
                      </h3>
                      <div className="mt-2 text-sm text-yellow-700">
                        <p>This will sync the flow <strong>"{flowToSync?.name}"</strong> to production.</p>
                        <p className="mt-2">This action will:</p>
                        <ul className="list-disc list-inside mt-1 space-y-1">
                          <li>Overwrite the production version completely</li>
                          <li>Include all flow steps and configurations</li>
                          <li>Create a backup for rollback (contact support if needed)</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="text-sm text-gray-600">
                  <p><strong>Flow:</strong> {flowToSync?.name}</p>
                  <p><strong>Steps:</strong> {flowToSync?.steps?.length || 0}</p>
                  <p><strong>Status:</strong> {flowToSync?.active ? 'Active' : 'Inactive'}</p>
                </div>

                {/* Error Message */}
                {error && (
                  <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-lg text-sm">
                    {error}
                  </div>
                )}

                {/* Action Buttons */}
                <div className="flex justify-end space-x-3">
                  <button
                    onClick={handleCancelSync}
                    className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                    disabled={syncLoading}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleConfirmSync}
                    disabled={syncLoading}
                    className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    {syncLoading ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        Syncing...
                      </>
                    ) : (
                      <>
                        🚀 Sync to Production
                      </>
                    )}
                  </button>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                {/* Success Message */}
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <span className="text-green-500 text-lg">✅</span>
                    </div>
                    <div className="ml-3">
                      <h3 className="text-sm font-medium text-green-800">
                        Sync Successful
                      </h3>
                      <div className="mt-2 text-sm text-green-700">
                        <p>{syncResult.message}</p>
                        <div className="mt-2">
                          <p><strong>Flow:</strong> {syncResult.data?.sync?.flowName}</p>
                          <p><strong>Steps synced:</strong> {syncResult.data?.sync?.stepsCount}</p>
                          <p><strong>Synced at:</strong> {new Date(syncResult.data?.sync?.syncedAt).toLocaleString()}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="text-center">
                  <p className="text-sm text-gray-600">This modal will close automatically in a few seconds.</p>
                  <button
                    onClick={handleCancelSync}
                    className="mt-3 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                  >
                    Close
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default FlowsManager; 